import { useEffect, useState } from 'react';
import api from '../services/api';
import KpiCard from '../components/KpiCard';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// Affiche un ratio sous forme de pourcentage, avec garde-fou division par zéro
const ratio = (num, den) => {
  if (!den || den === 0) return '—';
  const pct = (num / den) * 100;
  return `${pct.toFixed(1)} %`;
};

const NegBadge = ({ s }) => {
  const cfg = {
    paid:             ['#dbeafe','#1e40af','💰 Payé'],
    validated:        ['#d1fae5','#065f46','✓ Validé'],
    pending:          ['#fef9c3','#854d0e','⏳ En attente'],
    countered:        ['#fff7ed','#c2410c','↩ Contre-offre'],
    client_countered: ['#f5f3ff','#6d28d9','↩ C. client'],
    cancelled:        ['#f1f5f9','#64748b','✕ Annulé'],
  };
  const [bg, color, label] = cfg[s] || ['#f1f5f9','#64748b', s || '—'];
  return <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{label}</span>;
};

export default function Operations() {
  const [stats, setStats] = useState(null);
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/negotiations').catch(() => ({ data: [] })),
    ])
      .then(([statsRes, negRes]) => {
        setStats(statsRes.data);
        setNegotiations(Array.isArray(negRes.data) ? negRes.data : (negRes.data?.negotiations || []));
      })
      .catch(e => setErr(e.response?.data?.message || 'Impossible de charger les données'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
      <div className="spinner" />
    </div>
  );

  if (err) return (
    <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--danger)' }}>
      ⚠️ {err}
    </div>
  );

  const activeListings   = Number(stats?.services_stats?.active || 0);
  const totalListings    = Number(stats?.services_stats?.total || 0);
  const totalNegotiations = Number(stats?.negotiations?.total || 0);
  const ongoingNegotiations = Number(stats?.negotiations?.ongoing || 0);
  const dealsClosed      = Number(stats?.negotiations?.deals_closed || 0);

  const ongoingList = negotiations
    .filter(n => ['pending', 'countered', 'client_countered'].includes(n.status))
    .slice(0, 8);

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Opérations</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Annonces actives, négociations en cours et ratios d'engagement
        </p>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16, marginBottom: 28 }}>
        <KpiCard
          label="Annonces actives"
          value={fmt(activeListings)}
          sublabel={`${fmt(totalListings)} au total`}
          icon="📋"
          color="#3b82f6"
        />
        <KpiCard
          label="Négociations en cours"
          value={fmt(ongoingNegotiations)}
          sublabel={`${fmt(totalNegotiations)} négos au total`}
          icon="💬"
          color="#f59e0b"
        />
        <KpiCard
          label="Ratio négos / annonces"
          value={ratio(totalNegotiations, activeListings)}
          sublabel="Engagement par annonce"
          icon="📊"
          color="#8b5cf6"
        />
        <KpiCard
          label="Taux de conclusion"
          value={ratio(dealsClosed, totalNegotiations)}
          sublabel={`${fmt(dealsClosed)} deals conclus`}
          icon="🤝"
          color="#10b981"
        />
      </div>

      {/* Liste des négos en cours */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>Négociations en cours</h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {ongoingList.length} affichées sur {ongoingNegotiations}
            </span>
          </div>

          {ongoingList.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Aucune négociation en cours
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={th}>#</th>
                    <th style={th}>Client</th>
                    <th style={th}>Prestataire</th>
                    <th style={th}>Service</th>
                    <th style={{ ...th, textAlign: 'right' }}>Prix proposé</th>
                    <th style={th}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {ongoingList.map(n => (
                    <tr key={n.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={td}>#{n.id}</td>
                      <td style={td}>{n.client_name || `User ${n.client_id}`}</td>
                      <td style={td}>{n.provider_name || `User ${n.provider_id}`}</td>
                      <td style={td}>{n.service_title || n.service_name || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                        {fmt(n.last_offer_price || n.client_price)} F
                      </td>
                      <td style={td}><NegBadge s={n.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const th = { padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' };
const td = { padding: '12px 8px', color: 'var(--text-primary)' };
